import { base44 } from "@/api/base44Client";

/**
 * Single source of truth for marketing package pricing.
 *
 * Given a list of marketing Packages records, fetches any linked Package
 * product records (via product_id) and returns a new list where each pkg's
 * original_price and sale_price are resolved live from the product record:
 *   - original_price <- product.price_usd
 *   - sale_price     <- product.discount_price_usd
 *
 * When product_id is empty (or the linked product can't be found), the
 * marketing record's own price fields are preserved unchanged.
 *
 * Any future consumer of the Packages entity should call this before
 * rendering prices.
 */
export async function resolvePackagePrices(packages) {
  if (!packages || packages.length === 0) return packages || [];
  const productIds = packages.map((p) => p.product_id).filter(Boolean);
  if (productIds.length === 0) return packages;
  try {
    const products = await base44.entities.Package.filter(
      { id: { $in: productIds } },
      "name",
      100
    );
    const productMap = new Map(products.map((p) => [p.id, p]));
    return packages.map((pkg) => {
      if (!pkg.product_id) return pkg;
      const product = productMap.get(pkg.product_id);
      if (!product) return pkg;
      if (product.discount_price_usd != null) {
        return {
          ...pkg,
          original_price: product.price_usd ?? pkg.original_price,
          sale_price: product.discount_price_usd,
          _price_source: "product",
          _component_ids: product.package_component_ids || [],
          logo_url: product.logo_url || "",
          brand_logo_url: product.brand_logo_url || "",
        };
      }
      return {
        ...pkg,
        original_price: null,
        sale_price: product.price_usd ?? pkg.sale_price,
        _price_source: "product",
        _component_ids: product.package_component_ids || [],
        logo_url: product.logo_url || "",
        brand_logo_url: product.brand_logo_url || "",
      };
    });
  } catch {
    return packages;
  }
}

/**
 * Resolves each marketing package's component outlets (the platform Package
 * records referenced by the linked product's package_component_ids) so cards
 * can render them as logo chips.
 *
 * Relies on `_component_ids` surfaced by resolvePackagePrices (reusing the
 * same product fetch — no duplicate request). Batch-fetches all referenced
 * component records in ONE query and attaches, to each marketing package, a
 * `components` array of { id, name, logo_url, brand_logo_url, link_type } in
 * the order of package_component_ids. Packages with no linked product or no
 * components get components: []. The transient `_component_ids` field is
 * stripped from the returned packages.
 */
export async function resolvePackageComponents(packages) {
  if (!packages || packages.length === 0) return packages || [];
  const allComponentIds = Array.from(
    new Set(packages.flatMap((p) => p._component_ids || []))
  ).filter(Boolean);
  if (allComponentIds.length === 0) {
    return packages.map(({ _component_ids, ...rest }) => ({ ...rest, components: [] }));
  }
  try {
    const components = await base44.entities.Package.filter(
      { id: { $in: allComponentIds } },
      "name",
      500
    );
    const componentMap = new Map(
      components.map((c) => [
        c.id,
        {
          id: c.id,
          name: c.name,
          logo_url: c.logo_url,
          brand_logo_url: c.brand_logo_url,
          link_type: c.link_type,
          category: c.category,
          media_outlets_count: c.media_outlets_count,
          distribution_turnaround: c.distribution_turnaround,
        },
      ])
    );
    // Category order for chip display: guaranteed_placements first, then
    // newswires, then all others (pr_distribution and any other category)
    // last. Stable sort preserves package_component_ids order within each
    // group (Array.prototype.sort is stable per ES2019).
    const categoryRank = (cat) =>
      cat === "guaranteed_placements" ? 0 : cat === "newswires" ? 1 : 2;
    return packages.map(({ _component_ids, ...rest }) => {
      const ids = _component_ids || [];
      const resolvedComponents = ids
        .map((id) => componentMap.get(id))
        .filter(Boolean)
        .sort((a, b) => categoryRank(a.category) - categoryRank(b.category));
      return { ...rest, components: resolvedComponents };
    });
  } catch {
    return packages.map(({ _component_ids, ...rest }) => ({ ...rest, components: [] }));
  }
}